import { useState, useEffect, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Thumbs, FreeMode } from "swiper/modules";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/thumbs";

import "photoswipe/style.css";
import "./ProductGallery.css";
import { Gallery, Item } from "react-photoswipe-gallery";

const ProductGallery = ({ product }) => {
  const [activeThumbs, setActiveThumbs] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);
  const [mainSwiper, setMainSwiper] = useState(null);

  // refs for each zoom item
  const zoomRefs = useRef([]);

  const fallbackImage =
    "https://amzio.de/api/uploads/products/no-thumbnail.png";

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1200);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 🔥 Trigger zoom for the active slide
  const openZoomForActiveSlide = () => {
    if (!mainSwiper) return;

    const activeIndex = mainSwiper.activeIndex;

    // open correct zoom item
    const item = zoomRefs.current[activeIndex];
    if (item && item.open) item.open();
  };

  return (
    <Gallery>
      <div className="row align-items-start gap-3 gap-lg-0 mt-3">
        {/* ====================================================== */}
        {/* DESKTOP */}
        {/* ====================================================== */}
        {!isMobile && (
          <>
            {/* Thumbs */}
            <div className="col-12 col-xl-2">
              <Swiper
                onSwiper={setActiveThumbs}
                direction="vertical"
                spaceBetween={10}
                slidesPerView={4}
                freeMode
                watchSlidesProgress
                slideToClickedSlide
                modules={[FreeMode, Thumbs]}
                className="thumbSwiper d-none d-xl-block"
                style={{ height: "400px" }}
              >
                {product?.images?.map((img, i) => (
                  <SwiperSlide key={i}>
                    <img
                      src={img}
                      alt=""
                      className="w-100"
                      onError={(e) => (e.target.src = fallbackImage)}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Main Slider */}
            <div className="col-12 col-xl-10">
              <Swiper
                onSwiper={setMainSwiper}
                modules={[Thumbs]}
                thumbs={{ swiper: activeThumbs }}
                spaceBetween={10}
                className="mainSwiper"
              >
                {product?.images?.map((img, i) => (
                  <SwiperSlide key={i}>
                    <div style={{ position: "relative" }}>
                      <img
                        src={img}
                        className="w-100"
                        onError={(e) => (e.target.src = fallbackImage)}
                      />
                    </div>
                  </SwiperSlide>
                ))}

                {/* Fixed Zoom Button */}
                <button
                  onClick={openZoomForActiveSlide}
                  className="fixed-zoom-btn"
                  style={{
                    position: "absolute",
                    bottom: "8px",
                    right: "8px",
                    background: "#00000088",
                    color: "#fff",
                    border: "none",
                    padding: "9px 14px",
                    borderRadius: "50%",
                    cursor: "pointer",
                    zIndex: 10,
                  }}
                >
                  <i className="fa-solid fa-magnifying-glass"></i>
                </button>
              </Swiper>
            </div>
          </>
        )}

        {/* ====================================================== */}
        {/* MOBILE */}
        {/* ====================================================== */}
        {isMobile && (
          <>
            <div className="col-12">
              <Swiper
                onSwiper={setMainSwiper}
                modules={[Thumbs]}
                thumbs={{ swiper: activeThumbs }}
                spaceBetween={10}
                className="mainSwiper"
              >
                {product?.images?.map((img, i) => (
                  <SwiperSlide key={i}>
                    <div style={{ position: "relative" }}>
                      <img
                        src={img}
                        className="w-100"
                        onError={(e) => (e.target.src = fallbackImage)}
                      />
                    </div>
                  </SwiperSlide>
                ))}

                <button
                  onClick={openZoomForActiveSlide}
                  className="fixed-zoom-btn"
                  style={{
                    position: "absolute",
                    bottom: "8px",
                    right: "8px",
                    background: "#00000088",
                    color: "#fff",
                    border: "none",
                    padding: "9px 14px",
                    borderRadius: "50%",
                    cursor: "pointer",
                    zIndex: 10,
                  }}
                >
                  <i className="fa-solid fa-magnifying-glass"></i>
                </button>
              </Swiper>
            </div>

            {/* Thumbs */}
            <div className="col-12 mb-3">
              <Swiper
                onSwiper={setActiveThumbs}
                direction="horizontal"
                spaceBetween={10}
                slidesPerView={4}
                freeMode
                watchSlidesProgress
                slideToClickedSlide
                modules={[FreeMode, Thumbs]}
                className="thumbSwiper mt-3"
              >
                {product?.images?.map((img, i) => (
                  <SwiperSlide key={i}>
                    <img
                      src={img}
                      className="w-100"
                      onError={(e) => (e.target.src = fallbackImage)}
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </>
        )}
      </div>

      {/* ================================================ */}
      {/* Hidden Zoom Items (Photoswipe gallery images) */}
      {/* ================================================ */}
      {product?.images?.map((img, i) => (
        <Item
          key={i}
          original={img}
          thumbnail={img}
          width={1500}
          height={1500}
        >
          {({ ref, open }) => {
            zoomRefs.current[i] = { ref, open };
            return (
              <span
                ref={ref}
                style={{ display: "none" }} // invisible
              />
            );
          }}
        </Item>
      ))}
    </Gallery>
  );
};

export default ProductGallery;
